'use client';

import React, { useContext, useMemo } from 'react';
import { BloodVisionPlan, BloodVisionAddOn } from './types/bloodVision';
import { Divider } from './FeatureList';
import { useBloodVision } from '../BloodVisionContext';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import { ZipCodeContext } from './ZipCodeContext';
import {
  getCheckoutHint,
  getFirstIncompleteCheckoutStep,
  scrollToCheckoutStep,
} from './checkoutGuidance';
import styles from './OrderSummary.module.css';

// We maintain the props interface for flexibility
interface OrderSummaryProps {
  mainPlan: BloodVisionPlan | null;
  selectedAddOns: BloodVisionAddOn[];
  total: number | null;
  onCheckout: () => void;
  isLoading: boolean;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  mainPlan: propMainPlan,
  selectedAddOns: propSelectedAddOns,
  onCheckout: propOnCheckout,
  isLoading: propIsLoading,
}) => {
  // Use hasCheckedOut and setHasCheckedOut from context
  const {
    selectedPlan: contextSelectedPlan,
    addOns,
    selectedAddOns: contextSelectedAddOns,
    handleCheckout,
    isLoading: contextIsLoading,
    zipCode,
    checkoutAllowed,
    hasCheckedOut,
    setHasCheckedOut,
    isZipValid,
    // availableModalityOptions,
    selectedModality,
    isHsaFsaIncluded,
    uiComponents,
    showSelectionRequiredHint,
  } = useBloodVision();

  // Get refs from context
  const { zipCodeInputRef, modalitySectionRef, hsaSectionRef } =
    useContext(ZipCodeContext);

  // Use props or context (props take precedence)
  const mainPlan = propMainPlan || contextSelectedPlan;
  const selectedAddOnsArray =
    propSelectedAddOns ||
    (contextSelectedAddOns
      ? addOns.filter((addon) => contextSelectedAddOns.includes(addon.id))
      : []);
  const onCheckout = propOnCheckout || handleCheckout;
  const isLoading =
    propIsLoading !== undefined ? propIsLoading : contextIsLoading;

  // For the checkout button, use isValidUSZip which checks if it's a valid US ZIP
  const isButtonDisabled = isLoading || !checkoutAllowed || hasCheckedOut;

  const isCampaignPlan = mainPlan?.is_plan_campaign === true;
  const missingStep = !checkoutAllowed
    ? getFirstIncompleteCheckoutStep({
        zipCode,
        isZipValid,
        selectedModality,
        isHsaFsaIncluded,
        uiComponents,
        isCampaignActive: isCampaignPlan,
      })
    : null;
  const checkoutHint = getCheckoutHint(missingStep);

  // Debug log
  // console.log('OrderSummary button state:', {
  //   zipCode,
  //   zipCodeLength: zipCode?.length,
  //   isZipValid,
  //   isValidUSZip,
  //   isButtonDisabled,
  // });

  // New method for handling wrapper clicks
  const handleWrapperClick = () => {
    if (!isButtonDisabled) return;

    scrollToCheckoutStep(missingStep, {
      zipCodeInputRef,
      modalitySectionRef,
      hsaSectionRef,
      showSelectionRequiredHint,
    });
  };

  // Memoized calculations for better performance
  const priceCalculations = useMemo(() => {
    const basePrice =
      mainPlan?.price_split?.base_price || mainPlan?.original_price || 0;
    const locationCharge = mainPlan?.price_split?.location_charge || 0;
    const atHomeCharges = mainPlan?.price_split?.at_home_charges || 0;
    const discount = mainPlan?.price_split?.discount || 0;

    const totalBeforeDiscount = basePrice + locationCharge + atHomeCharges;
    const finalTotal = totalBeforeDiscount - discount;

    // For campaign plans, override prices to 0
    if (isCampaignPlan) {
      return {
        basePrice,
        locationCharge,
        atHomeCharges,
        discount,
        totalBeforeDiscount,
        finalTotal: 0,
        displayPrice: 0,
      };
    }

    return {
      basePrice,
      locationCharge,
      atHomeCharges,
      discount,
      totalBeforeDiscount,
      finalTotal,
      displayPrice: finalTotal,
    };
  }, [mainPlan, isCampaignPlan]);

  // Handler for button click that disables the button and calls onCheckout
  const handleButtonClick = () => {
    // Only set hasCheckedOut for non-campaign plans
    // Campaign plans should allow re-clicking the button
    if (!isCampaignPlan) {
      setHasCheckedOut(true);
    }
    onCheckout();
  };

  if (!mainPlan) {
    return null;
  }

  return (
    <div className={styles.summaryContainer} id="order-summary-bv-card">
      {/* {contextSelectedPlan?.description ? (
        <SummaryHeader>{contextSelectedPlan?.description}</SummaryHeader>
      ) : null} */}

      <div className={styles.summaryContent}>
        <h4 className={styles.productTitle}>Blood Vision</h4>

        <div className={styles.lineItem}>
          <div className={styles.lineItemLabel}>
            {mainPlan?.name || 'Blood Vision: Annual Plan'}
          </div>
          <div className={styles.lineItemPrice}>
            {isCampaignPlan && (
              <span className={styles.strikethroughPrice}>
                ${priceCalculations.basePrice.toFixed(1)}
              </span>
            )}
            <span>
              ${isCampaignPlan ? '0.0' : priceCalculations.basePrice.toFixed(1)}
            </span>
          </div>
        </div>

        {/* Location Charge - Hide for campaign plans */}
        {!isCampaignPlan && priceCalculations.locationCharge > 0 && (
          <>
            <Divider pixelPadding={'16px'} />
            <div className={styles.lineItem}>
              <div className={styles.lineItemLabel}>Location Charge</div>
              <div className={styles.lineItemPrice}>
                +${priceCalculations.locationCharge.toFixed(1)}
              </div>
            </div>
          </>
        )}

        {/* At Home Charges - Hide for campaign plans */}
        {!isCampaignPlan && priceCalculations.atHomeCharges > 0 && (
          <>
            <Divider pixelPadding={'16px'} />
            <div className={styles.lineItem}>
              <div className={styles.lineItemLabel}>At Home Charges</div>
              <div className={styles.lineItemPrice}>
                +${priceCalculations.atHomeCharges.toFixed(1)}
              </div>
            </div>
          </>
        )}
        <Divider pixelPadding={'16px'} />
        {/* Discount - Hide for campaign plans */}
        {!isCampaignPlan && priceCalculations.discount > 0 ? (
          <>
            <div className={styles.lineItem}>
              <div className={styles.lineItemLabel}>Discount</div>
              <div className={styles.lineItemPrice}>
                -${priceCalculations.discount.toFixed(1)}
              </div>
            </div>
            <Divider pixelPadding={'16px'} />
          </>
        ) : null}

        {selectedAddOnsArray.map((addOn) => (
          <React.Fragment key={addOn.id}>
            <div className={styles.lineItem}>
              <div className={styles.lineItemLabel}>{addOn.name}</div>
              <div className={styles.lineItemPrice}>
                {addOn.price.includes('.') ? addOn.price : addOn.price + '.0'}
              </div>
            </div>
            <Divider pixelPadding={'16px'} />
          </React.Fragment>
        ))}

        <div className={styles.totalContainer}>
          <span>Total</span>
          <div className={styles.lineItemPrice}>
            ${priceCalculations.finalTotal.toFixed(1)}
          </div>
        </div>
        <Divider pixelPadding={'16px'} />
        {/* <TaxNote>* Taxes are calculated at checkout</TaxNote> */}
      </div>

      {/* Pass the disabled state to the wrapper to conditionally modify behavior */}
      <div
        className={styles.buttonWrapper}
        onClick={handleWrapperClick}
        data-disabled={isButtonDisabled}
      >
        <PrimaryButton onClick={handleButtonClick} disabled={isButtonDisabled}>
          Proceed to pay
        </PrimaryButton>
      </div>

      {checkoutHint && (
        <p className={styles.hintText} aria-live="polite">
          {checkoutHint}
        </p>
      )}
    </div>
  );
};

export default OrderSummary;
